// Copyright (c) StrangeBee 2024

import THTag from '@Atoms/THTag/THTag';
import { StatusContext } from '@Context/StatusProvider';
import { classList } from '@Helpers/className';
import { useLocalMessage } from '@Helpers/locale';
import { cutString } from '@Helpers/string';
import { formatTags } from '@Helpers/tags';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import React, { ReactElement, useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { TagOutput } from 'thehive-sdk';

import './DisplayTag.scss';

interface DisplayTagProps {
    tag: TagOutput | string;
    color?: string;
    className?: string;
    full?: boolean;
    ellipsis?: boolean;
    onClick?: (tag: string, event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
}

const DisplayTag = ({
    tag,
    color,
    full = false,
    onClick,
    className,
    ellipsis = true,
}: DisplayTagProps): ReactElement => {
    const tagValue = typeof tag === 'string' ? tag : formatTags(tag);
    const { config } = useContext(StatusContext);
    const colorValue = typeof tag === 'string' ? color : tag.colour;

    const cutValue = useMemo(() => cutString(tagValue, 32), [tagValue]);
    const instanceTag = (
        <THTag
            key={tagValue}
            onClick={(event): void => onClick?.(tagValue, event)}
            value={!ellipsis ? tagValue : cutValue}
            color={colorValue || config?.freeTagDefaultColour}
            full={full}
            className={classList('display-tag', className, { 'no-ellipsis': !ellipsis })}
        />
    );

    return tagValue !== cutValue ? (
        <THTooltip title={tagValue} placement="bottom">
            {instanceTag}
        </THTooltip>
    ) : (
        instanceTag
    );
};

interface DisplayTagListProps<T> {
    children: (data: any) => ReactElement;
    className?: string;
    limit?: number;
    data: T[];
}

const List = <T,>({ children, className, limit = Infinity, data }: DisplayTagListProps<T>): ReactElement => {
    const i = useLocalMessage();

    const currentLength = data.length;
    const [showMore, setShowMore] = useState<boolean>(false);
    const [dataTreated, setDataTreated] = useState<T[]>(data);

    const toggle = useCallback(() => {
        setShowMore((value) => !value);
    }, []);

    useEffect(() => {
        setDataTreated(data.slice(0, showMore ? Infinity : limit));
    }, [showMore, data]); // eslint-disable-line

    return (
        <span className={classList('display-custom-tags__wrap', className)}>
            <span className="display-custom-tags">
                {dataTreated?.map((item, index) => React.cloneElement(children(item), { key: index }))}
            </span>
            {limit < currentLength ? (
                <THButton type="link" onClick={toggle} className="display-custom-tags__extender">
                    {!showMore ? i('general.show-more') : i('general.show-less')}
                </THButton>
            ) : null}
        </span>
    );
};

DisplayTag.List = List;

export default DisplayTag;
